// src/pages/WebinarAugPage.tsx
// Webinar registration landing page at /webinar-aug ("Learn AI step by step,
// no fake promises" — ₹99, Aug 2026). Cloned from WebinarPage.tsx (July ₹9
// webinar, kept live) with repositioned copy + an added hype-vs-reality
// section. Light cream theme matching HomePage/PathPage. Page order: hero →
// the gap → what you'll learn → how it works → FAQ → registration form at the
// BOTTOM (nav button + mobile sticky bar jump to it). Flow: form →
// POST /api/webinar-register (row saved with UTMs) → redirect to the Razorpay
// payment link → Razorpay redirects to /webinar-aug/thanks. Config (date,
// links, price) lives in webinarAugConfig.ts.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WEBINAR } from './webinarAugConfig';

const LEARN_POINTS: Array<[string, string]> = [
  ['🗺️ Your step-by-step path into AI', 'The exact order to pick up AI skills so each one builds on the last — what to learn, what to skip, and where your programming experience gives you a head start.'],
  ['⚡ Live building, not slides', 'Watch a real AI application written and run live, so you see what building along a path actually looks like in practice.'],
  ['🧭 A filter for the hype', 'How to tell real skills from social-media promises — what genuinely moves you toward AI work, projects and confidence, and what wastes your months.'],
];

// Session agenda — the page itself demonstrates the step-by-step approach:
// each block builds on the previous one, mirroring cubemate.ai's path model.
const AGENDA: Array<[string, string, string]> = [
  ['7:00', 'Where you stand', 'Map the programming skills you already have onto the AI landscape — you\'re closer than your feed makes you feel.'],
  ['7:20', 'The path, step by step', 'The exact sequence into AI — each step unlocks the next, with honest timelines for every stage. The same path model we built into cubemate.ai.'],
  ['7:50', 'Build it live', 'A real AI application, written and run from scratch in front of you — so you see every step, not a finished demo.'],
  ['8:40', 'Your next 90 days', 'Turn the path into your personal plan: what to do next week, and the month after that. Open Q&A.'],
];

const HYPE_VS_REAL: Array<[string, string]> = [
  ['"Master AI in 30 days" with a countdown timer', 'An honest sequence: fundamentals first, in an order that compounds — with realistic timelines for each stage'],
  ['Salary screenshots and "₹40 LPA without coding"', 'What AI roles actually expect, and the shortest credible path to being interview-ready'],
  ['Lists of 47 tools you "must" learn this week', 'The small core of skills that transfer across every tool — learn once, use everywhere'],
  ['Copy-paste "projects" that teach you nothing', 'Small real builds you fully understand — the kind you can defend in an interview'],
];

const WebinarAugPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '', segment: 'working_dev' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Track UTM-tagged ad visits, same pattern as PathPage.tsx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (!hasUtm) return;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiUrl}/api/ad-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        landing_page: '/webinar-aug',
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        utm_term: params.get('utm_term') || undefined,
        gclid: params.get('gclid') || undefined,
        referrer: document.referrer || undefined,
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.visit_id) localStorage.setItem('ad_visit_id', String(data.visit_id));
      })
      .catch(() => { /* best-effort */ });
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    const params = new URLSearchParams(window.location.search);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const res = await fetch(`${apiUrl}/api/webinar-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webinar_slug: WEBINAR.slug,
          name: form.name,
          email: form.email,
          whatsapp: form.whatsapp,
          segment: form.segment,
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          referrer: document.referrer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Registration failed. Please try again.');
      }
      localStorage.setItem('webinar_reg_id', String(data.registration_id));
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'webinar_registration' });
        // Meta campaigns count "Website Completed Registration" — must fire alongside Lead
        window.fbq('track', 'CompleteRegistration', { content_name: 'webinar_registration' });
      }
      if (WEBINAR.googleAdsConversion && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: WEBINAR.googleAdsConversion });
      }
      if (WEBINAR.razorpayLink) {
        window.location.href = WEBINAR.razorpayLink;
      } else {
        navigate('/webinar-aug/thanks');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
      return;
    }
  };

  return (
    <div className="webinar-root">
      <style>{`
        .webinar-root {
          --bg: #fefae8;
          --bg-2: #ffffff;
          --bg-3: #fff5d1;
          --line: #1a1a2e15;
          --ink: #1a1a2e;
          --ink-soft: #40405a;
          --ink-mute: #6b6b85;
          --teal: #0d9488;
          --violet: #7c3aed;
          --p-gradient: linear-gradient(135deg, #14b8a6 0%, #8b5cf6 100%);
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--bg);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          color-scheme: light;
        }
        .webinar-root .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .webinar-root .w-grad-text {
          background: var(--p-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .webinar-root .w-grad-bg { background: var(--p-gradient); }
        .webinar-root .w-card {
          background: var(--bg-2);
          border: 1.5px solid var(--line);
          border-radius: 18px;
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 8px 24px -10px rgba(26,26,46,.08);
        }
        .webinar-root .w-input {
          width: 100%;
          background: #ffffff;
          border: 1.5px solid #1a1a2e26;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 16px; /* >=16px prevents iOS Safari auto-zoom on focus */
          color: var(--ink);
          outline: none;
          transition: border-color .2s;
        }
        .webinar-root .w-input:focus { border-color: #14b8a6; }
        .webinar-root .w-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 6px;
          color: var(--ink-soft);
        }
        @keyframes w-spin { to { transform: rotate(360deg); } }
        .webinar-root .w-spin { animation: w-spin 1s linear infinite; display: inline-block; }
      `}</style>

      {/* ───── NAV ───── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b" style={{ background: 'rgba(254,250,232,0.85)', borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-8 h-8 rounded-lg" />
            <span className="font-bold tracking-tight text-lg">Cubemate</span>
          </a>
          <a href="#register" className="px-4 py-2 text-sm font-semibold rounded-full w-grad-bg text-white hover:opacity-90 transition shadow-md">
            Reserve my seat →
          </a>
        </div>
      </nav>

      {/* ───── SECTION 1 — HERO (webinar info only) ───── */}
      <header className="pt-28 md:pt-32 pb-10 px-5 md:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs mono uppercase tracking-widest font-semibold mb-4" style={{ color: 'var(--teal)' }}>
            🔴 live · 2 hours · 7–9 pm ist · limited seats
          </p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.08] mb-5">
            Want to build with AI, but no clear path? <span className="w-grad-text">Your path starts here.</span>
          </h1>
          <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--ink-soft)' }}>
            {WEBINAR.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <span className="px-4 py-2 rounded-full text-sm font-bold border bg-white" style={{ borderColor: '#14b8a655', color: 'var(--teal)' }}>
              📅 {WEBINAR.dateDisplay}
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-bold text-white w-grad-bg">
              Entry {WEBINAR.entryPrice}
            </span>
          </div>
          <a href="#register" className="inline-block px-8 py-3.5 font-bold rounded-full w-grad-bg text-white hover:opacity-90 transition shadow-lg">
            Reserve my seat — {WEBINAR.entryPrice} →
          </a>
        </div>
      </header>

      {/* ───── SECTION 2 — THE GAP (hype vs what actually works) ───── */}
      <section className="py-12 px-5 md:px-6" style={{ background: 'linear-gradient(180deg, transparent, #fff5d180, transparent)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-3">The gap this session bridges</h2>
          <p className="text-center text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--ink-soft)' }}>
            Your feed is full of AI promises. Most of them are designed to sell, not to teach.
            Here's the difference — and the whole session is built on the right column.
          </p>
          <div className="w-card overflow-hidden">
            <div className="grid grid-cols-2 text-sm font-black border-b" style={{ borderColor: 'var(--line)' }}>
              <div className="px-4 md:px-6 py-3" style={{ color: '#b91c1c' }}>❌ What social media sells</div>
              <div className="px-4 md:px-6 py-3" style={{ color: 'var(--teal)' }}>✅ What actually works</div>
            </div>
            {HYPE_VS_REAL.map(([hype, real]) => (
              <div key={hype} className="grid grid-cols-2 text-sm md:text-base border-b last:border-b-0" style={{ borderColor: 'var(--line)' }}>
                <div className="px-4 md:px-6 py-4" style={{ color: 'var(--ink-mute)' }}>{hype}</div>
                <div className="px-4 md:px-6 py-4 font-medium" style={{ color: 'var(--ink-soft)' }}>{real}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── SECTION 3 — THE 2-HOUR PATH (session agenda) ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-3">The 2-hour path</h2>
          <p className="text-center text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--ink-soft)' }}>
            No wandering lecture — the session itself follows a path. Each block builds on the one before it.
          </p>
          <div className="space-y-4">
            {AGENDA.map(([time, title, desc]) => (
              <div key={time} className="w-card px-5 md:px-6 py-5 flex gap-4 md:gap-5 items-start">
                <span className="mono shrink-0 px-3 py-1.5 rounded-lg text-sm font-bold" style={{ background: 'var(--bg-3)', color: 'var(--teal)' }}>
                  {time}
                </span>
                <div>
                  <p className="font-bold mb-1">{title}</p>
                  <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── SECTION 4 — WHAT YOU'LL LEARN ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-8">What you'll walk away with</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {LEARN_POINTS.map(([title, desc]) => (
              <div key={title} className="w-card px-5 py-5">
                <p className="font-bold mb-1">{title}</p>
                <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── SECTION 5 — HOW IT WORKS ───── */}
      <section className="py-12 px-5 md:px-6" style={{ background: 'linear-gradient(180deg, transparent, #fff5d180, transparent)' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-center mb-8">How it works</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              ['1', 'Register & pay ' + WEBINAR.entryPrice, 'Fill the form, complete the payment — your seat is locked.'],
              ['2', 'Join the WhatsApp group', 'Reminders, the joining link and materials all land there.'],
              ['3', 'Show up live', 'No installs needed — just you, a browser, and 2 focused hours (7–9 PM IST).'],
            ].map(([n, title, desc]) => (
              <div key={n} className="w-card p-6 text-center">
                <div className="w-grad-bg text-white w-10 h-10 rounded-xl grid place-items-center text-lg font-extrabold mx-auto mb-3">{n}</div>
                <p className="font-bold mb-1">{title}</p>
                <p className="text-base" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FAQ ───── */}
      <section className="py-12 px-5 md:px-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {[
            ['Who is this for?', 'People who already program — working developers, students, career switchers — but aren\'t working in AI yet. If you can write basic code in any language, the path starts exactly where you are. No AI or ML experience needed.'],
            ['Is this another "become an AI engineer in 30 days" pitch?', 'No — that\'s exactly what this session is against. We\'ll tell you honestly what takes weeks and what takes months, so you leave with a realistic plan instead of a countdown timer.'],
            ['Do I need to install anything?', 'No. Everything happens live in the browser. A laptop is great for following along, but you can also just watch and ask questions.'],
            ['What is the ' + WEBINAR.entryPrice + ' for?', 'It reserves your seat and keeps the room serious. Free webinars get 90% no-shows; a small commitment means 2 focused hours with people who actually want to learn.'],
          ].map(([q, a]) => (
            <details key={q} className="w-card px-6 py-4 group">
              <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
                {q}
                <span className="transition group-open:rotate-45 text-lg" style={{ color: 'var(--teal)' }}>+</span>
              </summary>
              <p className="mt-3 text-base leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ───── REGISTRATION (bottom of page) ───── */}
      <section className="py-14 px-5 md:px-6">
        <div id="register" className="w-card p-6 md:p-8 max-w-md mx-auto scroll-mt-24">
          <h2 className="text-2xl font-black tracking-tight mb-1">Reserve your seat</h2>
          <p className="text-base mb-2" style={{ color: 'var(--ink-soft)' }}>
            📅 {WEBINAR.dateDisplay}
          </p>
          <p className="text-base mb-6" style={{ color: 'var(--ink-soft)' }}>
            {WEBINAR.entryPrice} entry · pay after registering. Joining details arrive on WhatsApp &amp; email.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="w-label" htmlFor="wb-name">Full name</label>
              <input id="wb-name" className="w-input" required maxLength={200} placeholder="Your name"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="w-label" htmlFor="wb-email">Email</label>
              <input id="wb-email" className="w-input" required type="email" maxLength={255} placeholder="you@example.com"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="w-label" htmlFor="wb-whatsapp">WhatsApp number</label>
              <input id="wb-whatsapp" className="w-input" required type="tel" maxLength={20} placeholder="+91 98765 43210"
                value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
            <div>
              <label className="w-label" htmlFor="wb-segment">Which best describes you?</label>
              <select id="wb-segment" className="w-input" value={form.segment}
                onChange={e => setForm({ ...form, segment: e.target.value })}>
                <option value="working_dev">Developer</option>
                <option value="career_switcher">Career switcher</option>
                <option value="student">Student (with programming knowledge)</option>
                <option value="other">Other</option>
              </select>
            </div>
            {status === 'error' && (
              <p className="text-sm font-semibold rounded-lg px-3 py-2" style={{ background: '#fee2e2', color: '#b91c1c' }}>{errorMsg}</p>
            )}
            <button type="submit" disabled={status === 'submitting'}
              className="w-full py-3.5 font-bold rounded-full w-grad-bg text-white hover:opacity-90 transition shadow-lg disabled:opacity-70">
              {status === 'submitting'
                ? <span><span className="w-spin">◐</span> Registering…</span>
                : `Reserve my seat — ${WEBINAR.entryPrice}`}
            </button>
            <p className="text-xs text-center" style={{ color: 'var(--ink-mute)' }}>
              Secure payment via Razorpay. No spam — only webinar updates.
            </p>
          </form>
        </div>
      </section>

      {/* ───── MOBILE STICKY CTA (hidden on desktop) ───── */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t backdrop-blur-md px-5 py-3 flex items-center justify-between gap-3"
        style={{ background: 'rgba(254,250,232,0.94)', borderColor: '#1a1a2e14' }}>
        <div className="min-w-0">
          <div className="font-black text-sm">Entry {WEBINAR.entryPrice}</div>
          <div className="text-xs truncate" style={{ color: 'var(--ink-mute)' }}>📅 {WEBINAR.dateDisplay}</div>
        </div>
        <a href="#register" className="shrink-0 px-5 py-2.5 rounded-full w-grad-bg text-white font-bold text-sm shadow-md">
          Reserve seat →
        </a>
      </div>

      {/* ───── FOOTER ───── */}
      <footer className="border-t pt-10 pb-24 lg:pb-10 px-6" style={{ borderColor: '#1a1a2e14' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ink-mute)' }}>
          <div className="flex items-center gap-2.5">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 rounded-lg" />
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Cubemate</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-black transition">Privacy</a>
            <a href="/terms" className="hover:text-black transition">Terms</a>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-mute)' }}>© 2026 Cubemate. Made in India.</p>
      </footer>
    </div>
  );
};

export default WebinarAugPage;
